import React, { useState, useEffect, useContext } from 'react'
import { FaPlus } from 'react-icons/fa6'
import { Link } from 'react-router'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './MembershipList.module.css'

const sanitizeNumber = (v) => {
    // handles "5,000", "₹1,200.50", null, undefined, "", "  "
    if (v === null || v === undefined) return 0
    const s = String(v)?.replace(/[^\d.-]/g, '') // strip non-numeric except dot & minus
    const n = Number(s)
    return Number.isFinite(n) ? n : 0
}

const safeDateMs = (v) => {
    // returns ms since epoch or 0 if invalid
    const t = v ? Date.parse(v) : NaN
    return Number.isFinite(t) ? t : 0
}

const safeLower = (v) => (typeof v === 'string' ? v.toLowerCase() : '')

const MembershipList = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const vendorIdentifier = JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier

    const [memberships, setMemberships] = useState([])
    const [filteredMemberships, setFilteredMemberships] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [sortBy, setSortBy] = useState('issuedDate') // align with UI options
    const [viewMode, setViewMode] = useState('grid')
    const [selectedMemberships, setSelectedMemberships] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [membershipOfferedCount, setMembershipOfferedCount] = useState(0)

    // Derived stats (safe)
    const totalRevenue = memberships?.reduce((sum, m) => sum + sanitizeNumber(m?.amountPaid), 0)
    const activeMembers = memberships?.filter(m => m?.status === 'active')?.length
    const avgEngagement =
        memberships?.length > 0
            ? Math.round(
                memberships?.reduce((sum, m) => sum + (Number(m?.engagement) || 0), 0) / memberships?.length
            )
            : 0

    // Filter + search + sort
    useEffect(() => {
        let filtered = [...memberships]

        // Search
        if (searchTerm?.trim()) {
            const q = searchTerm?.toLowerCase()
            filtered = filtered?.filter(m =>
                safeLower(m?.clientName)?.includes(q) ||
                safeLower(m?.cardTypeLabel)?.includes(q) ||
                safeLower(m?.categoryTypeLabel)?.includes(q) ||
                safeLower(m?.email)?.includes(q) ||
                safeLower(m?.name)?.includes(q) ||
                safeLower(m?.issuedCardNumber)?.includes(q)
            )
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered?.filter(m => (m?.status || '')?.toLowerCase() === statusFilter?.toLowerCase())
        }

        // Sort
        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return (a?.clientName || '').localeCompare(b?.clientName || '', undefined, {
                        sensitivity: 'base',
                        numeric: true,
                    })
                case 'revenue':
                    return sanitizeNumber(b?.amountPaid) - sanitizeNumber(a?.amountPaid)
                case 'engagement':
                    return (Number(b?.engagement) || 0) - (Number(a?.engagement) || 0)
                case 'issuedDate':
                    return safeDateMs(b?.issuedOn) - safeDateMs(a?.issuedOn)
                case 'expiryDate':
                    return safeDateMs(b?.expiryDate) - safeDateMs(a?.expiryDate)
                default:
                    // fallback to issued date
                    return safeDateMs(b?.issuedOn) - safeDateMs(a?.issuedOn)
            }
        })

        setFilteredMemberships(sorted)
    }, [memberships, searchTerm, statusFilter, sortBy])

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return styles.statusActive
            case 'expiring': return styles.statusExpiring
            case 'inactive': return styles.statusInactive
            default: return styles.statusDefault
        }
    }

    const getPlanColor = (planType) => {
        switch (planType) {
            case 'Enterprise': return styles.planEnterprise
            case 'Premium': return styles.planPremium
            case 'Basic': return styles.planBasic
            default: return styles.planDefault
        }
    }

    const toggleMemberSelection = (id) => {
        setSelectedMemberships(prev =>
            prev?.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )
    }

    // Select/Deselect all only for currently filtered rows
    const allOnPageSelected =
        filteredMemberships?.length > 0 &&
        filteredMemberships?.every(m => selectedMemberships.includes(m?.issuedCardNumber))

    const selectAllMembers = () => {
        if (allOnPageSelected) {
            // remove only those currently in the filtered list
            const idsOnPage = new Set(filteredMemberships?.map(m => m?.issuedCardNumber))
            setSelectedMemberships(prev => prev?.filter(id => !idsOnPage?.has(id)))
        } else {
            // add all filtered (preserve already selected)
            const idsToAdd = filteredMemberships?.map(m => m?.issuedCardNumber)
            setSelectedMemberships(prev => Array.from(new Set([...prev, ...idsToAdd])))
        }
    }

    const getAllMembers = async () => {
        setIsLoading(true);
        const response = await fetch(BASE_URL_API + "getMembershipData", {
            method: "POST",
            body: JSON.stringify({
                token: authCtx.token,
                storeIdentifier: activeStore?.storeIdentifier,
                vendorIdentifier: vendorIdentifier
            }),
        });

        if (!response.ok) {
            console.log("Something went wrong : Server Error");
        } else {
            const data = await response.json();
            if (data?.status === "success") {
                setMemberships(Array.isArray(data?.response) ? data?.response : [])
                setFilteredMemberships(Array.isArray(data?.response) ? data?.response : [])
                setMembershipOfferedCount(data?.membershipTypeCount)
            } else if (data.message === "tokenExpired") {
                authCtx.logout();
            } else {
                setMemberships([]);
                setFilteredMemberships([]);
            }
        }
        setIsLoading(false);
    };

    useEffect(() => {
        getAllMembers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function getNameInitials(fullName) {
        if (!fullName || typeof fullName !== "string") return "";
        const parts = fullName?.trim()?.split(/\s+/)?.filter(Boolean)
        if (parts.length === 0) return ""
        const firstInitial = parts[0][0]?.toUpperCase() || ""
        let secondInitial = ""
        if (parts.length === 1) {
            secondInitial = parts[0][1]?.toUpperCase() || ""
        } else if (parts.length === 2) {
            secondInitial = parts[1][0]?.toUpperCase() || ""
        } else {
            secondInitial = parts[1][0]?.toUpperCase() || ""
        }
        return (firstInitial + secondInitial)?.trim()
    }

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading your memberships...</p>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <h1 className={styles.title}>Memberships Dashboard</h1>
                    <p className={styles.subtitle}>Manage and track all your memberships in one place</p>
                </div>
                {membershipOfferedCount>0 ?
                <Link to={`/memberships/sell-membership`} className={styles.addButton}>
                    <FaPlus size={15} /> Sell A Membership
                </Link>
                :
                <Link to={`/memberships/settings`} className={styles.addButton}>
                    <FaPlus size={15} /> Create New Card
                </Link>
                }
            </div>

            {/* Stats Cards */}
            {membershipOfferedCount !== 0 ?
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>💰</div>
                        <div className={styles.statContent}>
                            <h3 className={styles.statValue}>₹{totalRevenue?.toLocaleString()}</h3>
                            <p className={styles.statLabel}>Total Revenue</p>
                            <span className={styles.statTrend}>+0% from last month</span>
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>👥</div>
                        <div className={styles.statContent}>
                            <h3 className={styles.statValue}>{activeMembers}</h3>
                            <p className={styles.statLabel}>Active Members</p>
                            <span className={styles.statTrend}>+ new this week</span>
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>⚡</div>
                        <div className={styles.statContent}>
                            <h3 className={styles.statValue}>{memberships?.length}</h3>
                            <p className={styles.statLabel}>Total Members</p>
                            <span className={styles.statTrend}>Growing strong</span>
                        </div>
                    </div>
                </div>
                :
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>💰</div>
                        <div className={styles.statContent}>
                            <h3 className={styles.statValue}>Let's start!</h3>
                            <p className={styles.statLabel}>Create your first membership card.</p>
                            <span className={styles.statTrend}>It just take less than 1 minutes to launch memberships with Lokaci Pro.</span>
                        </div>
                    </div>
                </div>
            }
            {/* Controls */}
            {membershipOfferedCount !== 0 &&
                <div className={styles.controls}>
                    <div className={styles.leftControls}>
                        <div className={styles.searchBox}>
                            <input
                                type="text"
                                placeholder="Search members, emails, plans..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                            <span className={styles.searchIcon}>🔍</span>
                        </div>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className={styles.filterSelect}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="expiring">Expiring Soon</option>
                            <option value="inactive">Inactive</option>
                        </select>

                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className={styles.sortSelect}
                        >
                            <option value="issuedDate">Sort by Issued Date</option>
                            <option value="expiryDate">Sort by Expiry Date</option>
                            <option value="name">Sort by Name</option>
                            <option value="revenue">Sort by Amount</option>
                        </select>
                    </div>

                    <div className={styles.rightControls}>
                        <div className={styles.viewToggle}>
                            <button
                                className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.active : ''}`}
                                onClick={() => setViewMode('grid')}
                            >
                                ⊞
                            </button>
                            <button
                                className={`${styles.viewBtn} ${viewMode === 'list' ? styles.active : ''}`}
                                onClick={() => setViewMode('list')}
                            >
                                ☰
                            </button>
                        </div>

                        {selectedMemberships.length > 0 && (
                            <div className={styles.bulkActions}>
                                <span className={styles.selectedCount}>
                                    {selectedMemberships?.length} selected
                                </span>
                                <button className={styles.bulkBtn}>Export</button>
                                <button className={styles.bulkBtn}>Archive</button>
                            </div>
                        )}
                    </div>
                </div>
            }
            {/* Members Grid/List */}
            <div className={`${styles.membersContainer} ${viewMode === 'list' ? styles.listView : styles.gridView}`}>
                {viewMode === 'list' && (
                    <div className={styles.listHeader}>
                        <div className={styles.headerCell}>
                            <input
                                type="checkbox"
                                checked={allOnPageSelected}
                                onChange={selectAllMembers}
                            />
                        </div>
                        <div className={styles.headerCell}>Member</div>
                        <div className={styles.headerCell}>Plan</div>
                        <div className={styles.headerCell}>Status</div>
                        <div className={styles.headerCell}>Revenue</div>
                        <div className={styles.headerCell}>Engagement</div>
                        <div className={styles.headerCell}>Actions</div>
                    </div>
                )}

                {filteredMemberships?.map((member) => (
                    <div
                        key={member?.issuedCardNumber}
                        className={`${styles.memberCard} ${selectedMemberships.includes(member?.issuedCardNumber) ? styles.selected : ''}`}
                    >
                        {viewMode === 'grid' ? (
                            <>
                                <div className={styles.cardHeader}>
                                    <input
                                        type="checkbox"
                                        checked={selectedMemberships.includes(member?.issuedCardNumber)}
                                        onChange={() => toggleMemberSelection(member?.issuedCardNumber)}
                                        className={styles.checkbox}
                                    />
                                    <div className={styles.memberAvatar}>{getNameInitials(member?.clientName)}</div>
                                    <div className={`${styles.statusBadge} ${getStatusColor(member?.status)}`}>
                                        {member.status}
                                    </div>
                                </div>

                                <div className={styles.cardBody}>
                                    <h3 className={styles.memberName}>{member?.clientName}</h3>
                                    <p className={styles.memberEmail}>{member?.email}</p>

                                    <div className={styles.planBadge}>
                                        <span className={`${styles.planTag} ${getPlanColor(member?.planType)}`}>
                                            {member?.categoryTypeLabel?.split(" ")[0]}
                                        </span>
                                        <span className={styles.revenue}>₹{sanitizeNumber(member?.amountPaid)?.toLocaleString()}</span>
                                    </div>

                                    <div className={styles.engagement}>
                                        <div className={styles.engagementBar}>
                                            <div
                                                className={styles.engagementFill}
                                                style={{ width: `${Number(member?.engagement) || 0}%` }}
                                            />
                                        </div>
                                        <span className={styles.engagementText}>{Number(member?.engagement) || 0}% engaged</span>
                                    </div>

                                    <div className={styles.memberInfo}>
                                        <span className={styles.startDate}>Started: {member?.issuedOn ? new Date(member?.issuedOn).toLocaleDateString() : '-'}</span>
                                        <span className={styles.lastActivity}>Expires: {member?.expiryDate || '-'}</span>
                                    </div>
                                </div>

                                <div className={styles.cardActions}>
                                    {/* <button className={styles.actionBtn}>View</button> */}
                                    <Link to={`/membership/${member?.issuedCardNumber}`} className={styles.actionBtn}>View</Link>
                                    <Link to={`/membership/${member?.issuedCardNumber}`} className={styles.actionBtn}>Edit</Link>
                                    <button className={styles.actionBtn}>⋯</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.listCell}>
                                    <input
                                        type="checkbox"
                                        checked={selectedMemberships.includes(member?.issuedCardNumber)}
                                        onChange={() => toggleMemberSelection(member?.issuedCardNumber)}
                                    />
                                </div>
                                <div className={styles.listCell}>
                                    <div className={styles.memberInfo}>
                                        <div className={styles.memberAvatar}>{getNameInitials(member?.clientName)}</div>
                                        <div>
                                            <div className={styles.memberName}>{member?.clientName}</div>
                                            <div className={styles.memberEmail}>{member?.email}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.listCell}>
                                    <span className={`${styles.planTag} ${getPlanColor(member?.planType)}`}>
                                        {member?.categoryTypeLabel?.split(" ")[0]}
                                    </span>
                                </div>
                                <div className={styles.listCell}>
                                    <span className={`${styles.statusBadge} ${getStatusColor(member?.status)}`}>
                                        {member?.status}
                                    </span>
                                </div>
                                <div className={styles.listCell}>
                                    <strong>₹{sanitizeNumber(member?.amountPaid)?.toLocaleString()}</strong>
                                </div>
                                <div className={styles.listCell}>
                                    <div className={styles.engagement}>
                                        <div className={styles.engagementBar}>
                                            <div
                                                className={styles.engagementFill}
                                                style={{ width: `${Number(member?.engagement) || 0}%` }}
                                            />
                                        </div>
                                        <span>{Number(member?.engagement) || 0}%</span>
                                    </div>
                                </div>
                                <div className={styles.listCell}>
                                    <div className={styles.listActions}>
                                        {/* <button className={styles.actionBtn}>View</button> */}
                                        <Link to={`/membership/${member?.issuedCardNumber}`} className={styles.actionBtn}>View</Link>
                                        <Link to={`/membership/${member?.issuedCardNumber}`} className={styles.actionBtn}>Edit</Link>
                                        <button className={styles.actionBtn}>⋯</button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {filteredMemberships.length === 0 && !isLoading && (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>🔍</div>
                    <h3>No memberships found</h3>
                    <p>Try adjusting your search or filters</p>
                </div>
            )}
        </div>
    )
}

export default MembershipList
